import {
  fireAndForgetAppLog,
  getBrowserAppLogContext,
} from "@/lib/api/services/app_logs.client";
import { getAppLogErrorDetails } from "@/lib/utils/app-log-error";
import { shouldSendPageViewLogs } from "@/lib/utils/app-log-env";

type NavigationType = "push" | "replace" | "traverse";
type AppLogInstrumentedWindow = Window &
  typeof globalThis & {
    __snappyFixAppLogsInitialized?: boolean;
  };

function logPageView(url: string, navigationType: NavigationType | "initial") {
  // Set NEXT_PUBLIC_APP_LOG_PAGE_VIEWS=true to re-enable — see
  // shouldSendPageViewLogs() for why this is off by default. The check lives
  // here rather than at the call site so it stays authoritative if page-view
  // logging is ever wired up from somewhere else too (e.g. the
  // onRouterTransitionStart hook below).
  if (!shouldSendPageViewLogs()) return;

  const targetUrl = new URL(url, window.location.href);
  const path = `${targetUrl.pathname}${targetUrl.search}`;

  fireAndForgetAppLog({
    ...getBrowserAppLogContext(),
    level: "info",
    event_type: "page_view",
    source: "instrumentation-client.tsx",
    status_code: 200,
    url: targetUrl.href,
    path,
    fingerprint: `page_view:${path}`,
    tags: ["client", "navigation"],
    metadata: {
      navigationType,
    },
  });
}

function logClientError(error: unknown, eventName: string) {
  const path = `${window.location.pathname}${window.location.search}`;
  const details = getAppLogErrorDetails(error, {
    name: "ClientError",
    message: `${eventName} on ${path}`,
    digestParts: ["client_error", eventName, path],
  });

  // `sampled` because this is the unbounded path: these two listeners catch
  // everything AdSense creatives throw, and a creative stuck in a loop would
  // otherwise POST on every iteration. Session sampling keeps whole error
  // sequences intact for the visitors it does report on, and the per-page cap
  // in fireAndForgetAppLog backstops even those.
  fireAndForgetAppLog(
    {
      ...getBrowserAppLogContext(),
      level: "error",
      event_type: "client_error",
      source: "instrumentation-client.tsx",
      status_code: 500,
      ...details,
      fingerprint: `client_error:${details.error_name}:${path}`,
      tags: ["client", eventName],
      metadata: {
        eventName,
      },
    },
    { sampled: true },
  );
}

/**
 * Initializes global event listeners and the initial page view log.
 * Wrapped in requestIdleCallback to prevent blocking the main thread during initial load/hydration.
 */
if (typeof window !== "undefined") {
  const initClientLogs = () => {
    const browserWindow = window as AppLogInstrumentedWindow;

    if (!browserWindow.__snappyFixAppLogsInitialized) {
      browserWindow.__snappyFixAppLogsInitialized = true;

      // Handle Initial Page View
      const logInitialPageView = () =>
        logPageView(window.location.href, "initial");

      if (document.readyState === "complete") {
        logInitialPageView();
      } else {
        window.addEventListener("load", logInitialPageView, { once: true });
      }

      // Global Error Listeners
      window.addEventListener("error", (event) => {
        logClientError(event.error || event.message, "window_error");
      });

      window.addEventListener("unhandledrejection", (event) => {
        logClientError(event.reason, "unhandled_rejection");
      });
    }
  };

  if ("requestIdleCallback" in window) {
    window.requestIdleCallback(initClientLogs, { timeout: 2000 });
  } else {
    // Fallback for browsers that don't support requestIdleCallback (e.g. older Safari)
    setTimeout(initClientLogs, 1000);
  }
}

// export function onRouterTransitionStart(
//   url: string,
//   navigationType: NavigationType,
// ) {
//   logPageView(url, navigationType);
// }

// import {
//   fireAndForgetAppLog,
//   getBrowserAppLogContext,
// } from "@/lib/api/services/app_logs.client";
// import { getAppLogErrorDetails } from "@/lib/utils/app-log-error";

// type AppLogInstrumentedWindow = Window &
//   typeof globalThis & {
//     __snappyFixAppLogsInitialized?: boolean;
//   };

// function logClientError(error: unknown, eventName: string) {
//   const path = `${window.location.pathname}${window.location.search}`;
//   const details = getAppLogErrorDetails(error, {
//     name: "ClientError",
//     message: `${eventName} on ${path}`,
//     digestParts: ["client_error", eventName, path],
//   });

//   fireAndForgetAppLog({
//     ...getBrowserAppLogContext(),
//     level: "error",
//     event_type: "client_error",
//     source: "instrumentation-client.tsx",
//     status_code: 500,
//     ...details,
//     fingerprint: `client_error:${details.error_name}:${path}`,
//     tags: ["client", eventName],
//     metadata: {
//       eventName,
//     },
//   });
// }

// /**
//  * Initializes global event listeners for client errors.
//  * Wrapped in requestIdleCallback to prevent blocking the main thread during initial load/hydration.
//  */
// if (typeof window !== "undefined") {
//   const initClientLogs = () => {
//     const browserWindow = window as AppLogInstrumentedWindow;

//     if (!browserWindow.__snappyFixAppLogsInitialized) {
//       browserWindow.__snappyFixAppLogsInitialized = true;

//       // Global Error Listeners
//       window.addEventListener("error", (event) => {
//         logClientError(event.error || event.message, "window_error");
//       });

//       window.addEventListener("unhandledrejection", (event) => {
//         logClientError(event.reason, "unhandled_rejection");
//       });
//     }
//   };

//   if ("requestIdleCallback" in window) {
//     window.requestIdleCallback(initClientLogs, { timeout: 2000 });
//   } else {
//     // Fallback for browsers that don't support requestIdleCallback (e.g. older Safari)
//     setTimeout(initClientLogs, 1000);
//   }
// }
